import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Card,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  DatePicker,
  message,
  Dropdown,
  Switch,
} from "antd";
import {
  SearchOutlined,
  DollarOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  getAdminConsumption,
  getUserNames,
} from "../../api/parkpass/parkpassWalletTransactions";
import { useSelector } from "react-redux";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const formatMad = (value) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseFloat(value || 0))} MAD`;

const CitizenConsumptionPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [totals, setTotals] = useState({
    ticketPurchases: 0,
    finePayments: 0,
    subscriptionPayments: 0,
    remainingBalance: 0,
  });
  const [pdfExporting, setPdfExporting] = useState(false);

  const [filters, setFilters] = useState({
    userId: undefined,
    search: undefined,
    matricule: undefined,
    nonZero: false,
  });
  const [dateRange, setDateRange] = useState([]);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    fetchConsumption();
    fetchUsers();

    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message: msg } = e.data;
      switch (status) {
        case "starting":
          setPdfExporting(true);
          break;
        case "generating":
          break;
        case "complete":
          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }
          setPdfExporting(false);
          break;
        case "error":
          message.error(`Erreur lors de la génération du PDF: ${msg}`);
          setPdfExporting(false);
          break;
        default:
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;
      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(`Erreur lors de la génération du fichier Excel: ${error}`);
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUserNames();
      setUsers(response || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const buildParams = (extra = {}, currentFilters = filters) => ({
    userId: currentFilters.userId,
    search: currentFilters.search,
    matricule: currentFilters.matricule,
    non_zero: currentFilters.nonZero || undefined,
    date_from:
      dateRange && dateRange[0]
        ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
        : undefined,
    date_to:
      dateRange && dateRange[1]
        ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
        : undefined,
    ...extra,
  });

  const fetchConsumption = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters
  ) => {
    try {
      setLoading(true);
      const response = await getAdminConsumption(
        buildParams({ page, pageSize }, currentFilters)
      );
      setRows(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
      });
      setTotals({
        ticketPurchases: response.total_ticket_purchases,
        finePayments: response.total_fine_payments,
        subscriptionPayments: response.total_subscription_payments,
        remainingBalance: response.total_remaining_balance,
      });
    } catch (error) {
      console.error("Error fetching consumption:", error);
      message.error("Erreur lors du chargement de la consommation");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchConsumption(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchConsumption(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      userId: undefined,
      search: undefined,
      matricule: undefined,
      nonZero: false,
    };
    setFilters(resetFilters);
    setDateRange([]);
    fetchConsumption(1, pagination.pageSize, resetFilters);
  };

  const handleNonZeroChange = (checked) => {
    const newFilters = { ...filters, nonZero: checked };
    setFilters(newFilters);
    fetchConsumption(1, pagination.pageSize, newFilters);
  };

  // --- Export (Web Worker-based, same skeleton as CitizenTransactionsPage) ---

  const fetchExportData = async () => {
    try {
      const response = await getAdminConsumption(
        buildParams({ no_pagination: true })
      );
      return Array.isArray(response) ? response : response.items || [];
    } catch (error) {
      console.error("Error fetching export data:", error);
      message.error("Erreur lors de la récupération des données d'export");
      return null;
    }
  };

  const buildFormattedData = (items) =>
    items.map((row) => ({
      user_full_name:
        row.user_full_name ||
        `${row.user_first_name || ""} ${row.user_last_name || ""}`.trim() ||
        "N/A",
      topups_total: parseFloat(row.topups_total || 0),
      ticket_purchases_total: parseFloat(row.ticket_purchases_total || 0),
      fine_payments_total: parseFloat(row.fine_payments_total || 0),
      subscription_payments_total: parseFloat(
        row.subscription_payments_total || 0
      ),
      remaining_balance: parseFloat(row.remaining_balance || 0),
    }));

  const headerDefs = [
    { text: "Client", type: "string", key: "user_full_name" },
    { text: "Recharge", type: "int", key: "topups_total", align: "right" },
    { text: "Achat ticket", type: "int", key: "ticket_purchases_total", align: "right" },
    { text: "Paiement amende", type: "int", key: "fine_payments_total", align: "right" },
    { text: "Paiement abonnement", type: "int", key: "subscription_payments_total", align: "right" },
    { text: "Solde restant", type: "int", key: "remaining_balance", align: "right" },
  ];

  // Totals for the report summary must be computed over the FULL exported data
  // (all wallets), not the current page's `totals` state.
  const computeExportTotals = (data) =>
    data.reduce(
      (acc, it) => ({
        topups: acc.topups + parseFloat(it.topups_total || 0),
        ticketPurchases: acc.ticketPurchases + parseFloat(it.ticket_purchases_total || 0),
        finePayments: acc.finePayments + parseFloat(it.fine_payments_total || 0),
        subscriptionPayments:
          acc.subscriptionPayments + parseFloat(it.subscription_payments_total || 0),
        remainingBalance: acc.remainingBalance + parseFloat(it.remaining_balance || 0),
      }),
      {
        topups: 0,
        ticketPurchases: 0,
        finePayments: 0,
        subscriptionPayments: 0,
        remainingBalance: 0,
      }
    );

  const summaryLine = (s) =>
    `Recharge: ${formatMad(s.topups)}\n` +
    `Achat ticket: ${formatMad(s.ticketPurchases)}\n` +
    `Paiement amende: ${formatMad(s.finePayments)}\n` +
    `Paiement abonnement: ${formatMad(s.subscriptionPayments)}\n` +
    `Solde restant: ${formatMad(s.remainingBalance)}`;

  const handlePdfClick = async (operation) => {
    setPdfExporting(true);
    const items = await fetchExportData();
    if (!items) {
      setPdfExporting(false);
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `citizen_consumption_${timestamp}.pdf`;
    const baseUrl = window.location.origin;
    const formattedData = buildFormattedData(items);

    const data = [
      [
        "Consommation Citizen Wallet",
        "",
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
      ],
      [],
      [],
      user?.full_name,
      [summaryLine(computeExportTotals(formattedData))],
      [headerDefs],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce(
          (sum, it) => sum + parseFloat(it.remaining_balance || 0),
          0
        ),
        label: "Total Solde restant",
      },
    ];

    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const handleExcelClick = async () => {
    setPdfExporting(true);
    const items = await fetchExportData();
    if (!items) {
      setPdfExporting(false);
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `citizen_consumption_${timestamp}.xlsx`;
    const baseUrl = window.location.origin;
    const formattedData = buildFormattedData(items);

    const data = [
      [
        "Consommation Citizen Wallet",
        "Format Général",
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
      ],
      [],
      [],
      user?.full_name,
      [summaryLine(computeExportTotals(formattedData))],
      [headerDefs],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce(
          (sum, it) => sum + parseFloat(it.remaining_balance || 0),
          0
        ),
        label: "Total Solde restant",
      },
    ];

    workerExcelRef.current.postMessage({ data, filename });
    setPdfExporting(false);
  };

  const getPdfMenuItems = () => [
    {
      key: "download",
      label: "Télécharger",
      onClick: () => handlePdfClick("download"),
      disabled: pdfExporting,
    },
    {
      key: "open",
      label: "Ouvrir",
      onClick: () => handlePdfClick("open"),
      disabled: pdfExporting,
    },
  ];

  // --- End Export ---

  const columns = [
    {
      title: "Client",
      key: "client",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.user_full_name ||
              `${record.user_first_name || ""} ${
                record.user_last_name || ""
              }`.trim() ||
              "N/A"}
          </div>
          {record.user_email && (
            <div style={{ fontSize: 12, color: "#999" }}>
              {record.user_email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Recharge",
      dataIndex: "topups_total",
      key: "topups_total",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.topups_total || 0) - parseFloat(b.topups_total || 0),
      render: (v) => formatMad(v),
    },
    {
      title: "Achat ticket",
      dataIndex: "ticket_purchases_total",
      key: "ticket_purchases_total",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.ticket_purchases_total || 0) -
        parseFloat(b.ticket_purchases_total || 0),
      render: (v) => formatMad(v),
    },
    {
      title: "Paiement amende",
      dataIndex: "fine_payments_total",
      key: "fine_payments_total",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.fine_payments_total || 0) -
        parseFloat(b.fine_payments_total || 0),
      render: (v) => formatMad(v),
    },
    {
      title: "Paiement abonnement",
      dataIndex: "subscription_payments_total",
      key: "subscription_payments_total",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.subscription_payments_total || 0) -
        parseFloat(b.subscription_payments_total || 0),
      render: (v) => formatMad(v),
    },
    {
      title: "Solde restant",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.remaining_balance || 0) -
        parseFloat(b.remaining_balance || 0),
      render: (v) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>{formatMad(v)}</span>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Consommation Citizen Wallet
            </Title>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Rechercher par nom ou email du client..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Rechercher par matricule..."
                prefix={<SearchOutlined />}
                value={filters.matricule}
                onChange={(e) => handleFilterChange("matricule", e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                placeholder={["Date de début", "Date de fin"]}
                style={{ width: "100%" }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                showTime
                format="YYYY-MM-DD HH:mm"
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Sélectionner un client"
                style={{ width: "100%" }}
                value={filters.userId}
                onChange={(value) => handleFilterChange("userId", value)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((u) => (
                  <Option key={u.user_id} value={u.user_id}>
                    {u.full_name ||
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.email ||
                      `User ${u.user_id}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Rechercher
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Réinitialiser
                </Button>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Switch
                  checked={filters.nonZero}
                  onChange={handleNonZeroChange}
                />
                <span>Masquer les portefeuilles vides</span>
              </Space>
            </Col>
          </Row>
        </Space>
        <div style={{ marginBottom: 16, gap: 8 }} className="flex justify-end">
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExcelClick}
            disabled={rows.length === 0 || pdfExporting}
          >
            Export Excel
          </Button>
          <Dropdown menu={{ items: getPdfMenuItems() }} disabled={pdfExporting}>
            <Button icon={<FilePdfOutlined />} disabled={pdfExporting}>
              Export PDF{" "}
              {pdfExporting ? "..." : <span style={{ marginLeft: 5 }}>▼</span>}
            </Button>
          </Dropdown>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="wallet_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => (
              <span>
                {`Total: ${total} portefeuilles | `}
                <strong>
                  Achat ticket: {formatMad(totals.ticketPurchases)} | Paiement
                  amende: {formatMad(totals.finePayments)} | Paiement abonnement:{" "}
                  {formatMad(totals.subscriptionPayments)} | Solde restant:{" "}
                  {formatMad(totals.remainingBalance)}
                </strong>
              </span>
            ),
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default CitizenConsumptionPage;
